import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { motion, AnimatePresence } from 'framer-motion'
import { FaArrowDown, FaDownload, FaEnvelope, FaCode } from 'react-icons/fa'
import { useAdmin } from '../context/AdminContext'
import { useNavigate } from 'react-router'

const roles = ['Software Engineer', 'Full Stack Builder', 'Problem Solver']

export default function Hero() {
const { siteData, profileImage, resumeData, loading } = useAdmin()
  const { hero } = siteData

  const [roleIndex, setRoleIndex] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setRoleIndex((prev) => (prev + 1) % roles.length)
    }, 2500)
    return () => clearInterval(interval)
  }, [])

  const navigate = useNavigate()

  // Do not render until initial API load completes
  if (loading) return null

  const handleResume = () => {
    // Use uploaded resume if available, otherwise fall back to /resume.pdf
    if (resumeData?.dataUrl) {
      const a = document.createElement('a')
      const ownerName = hero.name?.replace(/\s+/g, '_') || 'Resume'
      a.href     = resumeData.dataUrl
      a.download = `${ownerName}_Resume.pdf`
      a.rel      = 'noopener noreferrer'
      document.body.appendChild(a); a.click(); document.body.removeChild(a)
    } else {
      // Fallback: static file in /public/resume.pdf
      const a = document.createElement('a')
      a.href     = '/resume.pdf'
      a.download = 'Shiv_Shankar_Pandit_Resume.pdf'
      a.rel      = 'noopener noreferrer'
      document.body.appendChild(a); a.click(); document.body.removeChild(a)
    }
  }

  const nameParts = (hero.name || 'Shiv Shankar Pandit').split(' ')
  const lastName  = nameParts.pop()
  const firstName = nameParts.join(' ')

  const container = { hidden: {}, visible: { transition: { staggerChildren: 0.13, delayChildren: 0.2 } } }
  const item = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.22,1,0.36,1] } } }

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
      <Helmet>
        <title>Shiv Shankar Pandit — Software Engineer</title>
        <meta name="description" content="Portfolio of Shiv Shankar Pandit, Full Stack Developer. Discover my work and get in touch." />
      </Helmet>

      {/* ── Ambient background ── */}
      <div className="absolute inset-0 pointer-events-none select-none">
        <div className="hero-orb-1 absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full blur-[130px] opacity-20"
          style={{ background: 'radial-gradient(circle,#3b82f6 0%,transparent 70%)' }} />
        <div className="hero-orb-2 absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full blur-[120px] opacity-15"
          style={{ background: 'radial-gradient(circle,#10b981 0%,transparent 70%)' }} />
        <div className="hero-orb-3 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full blur-[100px] opacity-10"
          style={{ background: 'radial-gradient(circle,#10b981 0%,transparent 70%)' }} />
        <div className="hero-grid absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage: 'linear-gradient(rgba(59,130,246,.6) 1px,transparent 1px),linear-gradient(90deg,rgba(59,130,246,.6) 1px,transparent 1px)',
            backgroundSize: '72px 72px',
          }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 w-full pt-24 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          {/* ── Left: text ── */}
          <motion.div variants={container} initial="hidden" animate="visible" className="order-2 lg:order-1">

            {hero.availableForWork && (
              <motion.div variants={item} className="mb-6">
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-mono font-medium"
                  style={{ background:'rgba(16,185,129,0.08)', border:'1px solid rgba(16,185,129,0.22)', color:'#10b981' }}>
                  <span className="glow-dot" />
                  Available for opportunities
                </span>
              </motion.div>
            )}

            <motion.h1 variants={item} className="font-display font-black leading-tight mb-3"
              style={{ fontSize:'clamp(2.6rem,6.5vw,5rem)', color:'var(--text-1)', letterSpacing:'-0.02em' }}>
              Hi, I'm <span className="gradient-text">{firstName} {lastName}</span>
            </motion.h1>

            <motion.div variants={item} className="mb-6 min-h-[3rem] flex items-center">
              <div className="font-display text-xl sm:text-2xl font-semibold" style={{ color:'var(--text-2)' }}>
                <AnimatePresence mode="popLayout">
                  <motion.div
                    key={roleIndex}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -20, opacity: 0 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="text-primary"
                  >
                    {roles[roleIndex]}
                  </motion.div>
                </AnimatePresence>
              </div>
            </motion.div>

            <motion.p variants={item} className="text-base leading-relaxed max-w-lg mb-8"
              style={{ color:'var(--text-2)' }}>
              {(!hero.tagline || hero.tagline === 'Software Engineer' || hero.tagline === hero.role) 
                ? "I build beautiful, scalable web applications with modern technologies. Passionate about creating seamless user experiences and writing clean, maintainable code." 
                : hero.tagline}
            </motion.p>

            <motion.div variants={item} className="flex flex-wrap gap-3">
              <motion.button onClick={() => navigate('/portfolio')}
                className="btn-primary flex items-center gap-2"
                whileHover={{ scale:1.04 }} whileTap={{ scale:0.97 }}>
                <FaCode size={13} /> View Portfolio
              </motion.button>
              <motion.button onClick={() => navigate('/contact')}
                className="btn-outline flex items-center gap-2"
                whileHover={{ scale:1.04 }} whileTap={{ scale:0.97 }}>
                <FaEnvelope size={12} /> Contact Me
              </motion.button>

              {/* Resume button — shows indicator when real PDF is uploaded */}
              <motion.button onClick={handleResume}
                className="btn-outline flex items-center gap-2 relative"
                whileHover={{ scale:1.04 }} whileTap={{ scale:0.97 }}>
                <FaDownload size={12} /> Resume
                {resumeData && (
                  <span className="absolute -top-1.5 -right-1.5 w-3 h-3 rounded-full bg-green-400"
                    style={{ boxShadow:'0 0 6px rgba(52,211,153,0.8)' }} />
                )}
              </motion.button>
            </motion.div>

            <motion.div variants={item} className="flex gap-8 mt-10 pt-8"
              style={{ borderTop:'1px solid var(--border)' }}>
              {(Array.isArray(hero.stats) ? hero.stats : [])
                .filter(s => !/[Ee]xperience|[Yy]ears/.test(s.label))
                .map(({ value, label }) => (
                  <div key={label}>
                    <p className="font-display font-black text-2xl gradient-text">{value}</p>
                    <p className="text-xs mt-0.5" style={{ color:'var(--text-3)' }}>{label}</p>
                  </div>
              ))}
            </motion.div>
          </motion.div>

          {/* ── Right: profile photo ── */}
          <motion.div
            initial={{ opacity:0, scale:0.88, x:40 }}
            animate={{ opacity:1, scale:1, x:0 }}
            transition={{ duration:0.85, delay:0.35, ease:[0.22,1,0.36,1] }}
            className="order-1 lg:order-2 flex justify-center lg:justify-end"
          >
            <div className="relative">
              <motion.div className="absolute inset-[-28px] rounded-full"
                style={{ border:'1px solid rgba(59,130,246,0.12)' }}
                animate={{ rotate:360 }} transition={{ duration:22, repeat:Infinity, ease:'linear' }} />
              <motion.div className="absolute inset-[-54px] rounded-full"
                style={{ border:'1px dashed rgba(16,185,129,0.07)' }}
                animate={{ rotate:-360 }} transition={{ duration:35, repeat:Infinity, ease:'linear' }} />

              <div className="absolute inset-0 rounded-[2rem] blur-[50px] opacity-35"
                style={{ background:'linear-gradient(135deg,#3b82f6,#10b981)' }} />

              {/* Photo frame — CIRCLE */}
              <div className="relative group w-64 h-64 sm:w-[300px] sm:h-[300px]">

  {/* 🔥 Outer soft glow */}
  <div
    className="absolute inset-0 rounded-full blur-[120px] opacity-40 transition-all duration-500 group-hover:opacity-70 group-hover:scale-110"
    style={{ background: 'linear-gradient(135deg,#3b82f6,#10b981)' }}
  />

  {/* 🔥 Inner strong glow */}
  <div
    className="absolute inset-0 rounded-full blur-[60px] opacity-60 transition-all duration-500 group-hover:opacity-100"
    style={{ background: 'linear-gradient(135deg,#3b82f6,#10b981)' }}
  />

  {/* ✅ Animated photo */}
  <motion.div
    className="relative w-full h-full rounded-full overflow-hidden border-2 transition-all duration-300 group-hover:scale-105"
    style={{
      borderColor: 'var(--border-mid)',
      boxShadow: '0 0 60px rgba(59,130,246,0.4), 0 0 120px rgba(16,185,129,0.3)',
    }}
    animate={{ y: [-8, 8, -8] }}
    transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
  >
    {profileImage ? (
      <img
        src={profileImage}
        alt={hero.name}
        className="w-full h-full object-cover object-center"
      />
    ) : (
      <div className="w-full h-full flex items-center justify-center bg-gray-800 text-white">
        No Image
      </div>
    )}
  </motion.div>

</div>

              {/* Floating badges removed */}
</div>
</motion.div>

        </div>



      </div>
    </section>
  )
}
